import type { PlayerInput } from '@nebulark/engine';

/**
 * Convert screen-space touch deltas into a unit-circle move vector.
 * Dead zone is tiny so gentle nudges register; saturation radius sets max speed.
 */
const DEAD_ZONE = 4;
const SATURATION_RADIUS = 35;

type ScreenXY = { x: number; y: number };

/**
 * Touch input adapter. Tracks one virtual stick (first non-bomb touch) plus
 * a bomb button hit-test supplied by the HUD owner.
 *
 * The move-stick operates entirely in screen pixel space: we record a drag
 * origin on touch start and turn the delta into a normalized vector.
 */
export class TouchInput {
  private moveTouchId: number | null = null;
  private bombTouchIds = new Set<number>();

  private dragOrigin: ScreenXY = { x: 0, y: 0 };
  private dragCurrent: ScreenXY = { x: 0, y: 0 };

  private moveX = 0;
  private moveY = 0;
  private bombPending = false;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    /**
     * Return true if a screen-space point is inside the bomb button.
     * Receives raw client coords (not canvas-local).
     */
    private readonly bombHitTest: (clientX: number, clientY: number) => boolean,
  ) {
    this.bind();
  }

  getCurrentInput(): PlayerInput {
    return {
      moveX: this.moveX,
      moveY: this.moveY,
      bomb: this.bombPending,
    };
  }

  consumeBomb(): void {
    this.bombPending = false;
  }

  dispose(): void {
    const c = this.canvas;
    c.removeEventListener('touchstart', this.onTouchStart);
    c.removeEventListener('touchmove', this.onTouchMove);
    c.removeEventListener('touchend', this.onTouchEnd);
    c.removeEventListener('touchcancel', this.onTouchEnd);
    c.removeEventListener('pointerdown', this.onPointerDown);
    c.removeEventListener('pointermove', this.onPointerMove);
    c.removeEventListener('pointerup', this.onPointerUp);
    c.removeEventListener('pointercancel', this.onPointerUp);
  }

  private bind(): void {
    const c = this.canvas;
    const opts: AddEventListenerOptions = { passive: false };
    c.addEventListener('touchstart', this.onTouchStart, opts);
    c.addEventListener('touchmove', this.onTouchMove, opts);
    c.addEventListener('touchend', this.onTouchEnd, opts);
    c.addEventListener('touchcancel', this.onTouchEnd, opts);
    // Desktop fallback.
    c.addEventListener('pointerdown', this.onPointerDown);
    c.addEventListener('pointermove', this.onPointerMove);
    c.addEventListener('pointerup', this.onPointerUp);
    c.addEventListener('pointercancel', this.onPointerUp);
  }

  private onTouchStart = (ev: TouchEvent): void => {
    ev.preventDefault();
    for (let i = 0; i < ev.changedTouches.length; i++) {
      const t = ev.changedTouches[i]!;
      if (this.bombHitTest(t.clientX, t.clientY)) {
        this.bombPending = true;
        this.bombTouchIds.add(t.identifier);
        continue;
      }
      if (this.moveTouchId === null) {
        this.moveTouchId = t.identifier;
        this.dragOrigin = { x: t.clientX, y: t.clientY };
        this.dragCurrent = { x: t.clientX, y: t.clientY };
        this.updateMoveVector();
      }
    }
  };

  private onTouchMove = (ev: TouchEvent): void => {
    ev.preventDefault();
    for (let i = 0; i < ev.changedTouches.length; i++) {
      const t = ev.changedTouches[i]!;
      if (this.moveTouchId !== null && t.identifier === this.moveTouchId) {
        this.dragCurrent = { x: t.clientX, y: t.clientY };
        this.updateMoveVector();
      }
    }
  };

  private onTouchEnd = (ev: TouchEvent): void => {
    ev.preventDefault();
    for (let i = 0; i < ev.changedTouches.length; i++) {
      const t = ev.changedTouches[i]!;
      if (this.moveTouchId !== null && t.identifier === this.moveTouchId) {
        this.moveTouchId = null;
        this.moveX = 0;
        this.moveY = 0;
      }
      if (this.bombTouchIds.has(t.identifier)) {
        this.bombTouchIds.delete(t.identifier);
      }
    }
  };

  // Desktop mouse fallback — treat pointer as a single-touch with a synthetic id.
  private pointerActive = false;
  private pointerMoveActive = false;

  private onPointerDown = (ev: PointerEvent): void => {
    if (ev.pointerType === 'touch') return;
    this.pointerActive = true;
    if (this.bombHitTest(ev.clientX, ev.clientY)) {
      this.bombPending = true;
      return;
    }
    if (!this.pointerMoveActive) {
      this.pointerMoveActive = true;
      this.dragOrigin = { x: ev.clientX, y: ev.clientY };
      this.dragCurrent = { x: ev.clientX, y: ev.clientY };
      this.updateMoveVector();
    }
  };

  private onPointerMove = (ev: PointerEvent): void => {
    if (ev.pointerType === 'touch') return;
    if (!this.pointerActive || !this.pointerMoveActive) return;
    this.dragCurrent = { x: ev.clientX, y: ev.clientY };
    this.updateMoveVector();
  };

  private onPointerUp = (ev: PointerEvent): void => {
    if (ev.pointerType === 'touch') return;
    this.pointerActive = false;
    if (this.pointerMoveActive) {
      this.pointerMoveActive = false;
      this.moveX = 0;
      this.moveY = 0;
    }
  };

  private updateMoveVector(): void {
    const dx = this.dragCurrent.x - this.dragOrigin.x;
    const dy = this.dragCurrent.y - this.dragOrigin.y;
    const len = Math.hypot(dx, dy);
    if (len <= DEAD_ZONE) {
      this.moveX = 0;
      this.moveY = 0;
      return;
    }
    const effective = Math.min(len, SATURATION_RADIUS);
    const scale = (effective - DEAD_ZONE) / (SATURATION_RADIUS - DEAD_ZONE);
    const ux = (dx / len) * scale;
    const uy = (dy / len) * scale;
    // Clamp to unit circle defensively.
    const ulen = Math.hypot(ux, uy);
    if (ulen > 1) {
      this.moveX = ux / ulen;
      this.moveY = uy / ulen;
    } else {
      this.moveX = ux;
      this.moveY = uy;
    }
  }
}
